import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, FileText, Image, Heart, Mail } from 'lucide-react';
import FlyerRequestModal from '@/components/FlyerRequestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const flyerCategories = [
  {
    id: 'friends-family',
    title: 'Friends & Family',
    desc: 'For loved ones, relatives, close friends, and personal circles who want to help share the journal and upcoming projects by word of mouth.',
  },
  {
    id: 'community',
    title: 'Community / Public',
    desc: 'Ideal for business owners, community spaces, public boards, showcase events, local outreach, and everyday places where people discover something new.',
  },
];

export default function SpreadTheWordPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div data-testid="spread-the-word-page">
      <FlyerRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* ===== SECTION 1: HERO ===== */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
              You Can Make a Difference
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-8 leading-tight">
              Spread the Word
            </h1>
            <div className="space-y-5 font-sans text-base md:text-lg text-[#7A736E] leading-relaxed max-w-2xl mx-auto">
              <p>
                Whether you are a person of deep faith or simply someone who believes in the power of encouragement, hope, and resilience &mdash; your support matters more than you know.
              </p>
              <p>
                Sometimes the smallest act carries the greatest weight. Sharing a story with a friend. Printing a flyer and leaving it where someone might find it. Sending a link to someone going through a difficult season. These are not small things. They are the quiet ways that something meaningful reaches the exact person who needs it most.
              </p>
              <p className="text-[#2A2421]/80 font-medium">
                You do not have to purchase anything to support what we are building. Sometimes simply sharing, printing, posting, or passing it along is the most powerful form of support there is.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION 2: MISSION ===== */}
      <section className="py-14 md:py-20 bg-[#2A2421]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mb-8" />
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-6">
              The Heart Behind the Mission
            </h2>
            <p className="font-sans text-base text-white/55 leading-relaxed mb-5">
              Led By Faith Studios exists to create books, journals, and projects that carry real testimony into the lives of real people. This brand was born from seasons of hardship, survival, and an unshakable belief that God wastes nothing &mdash; not the pain, not the tears, and not the story.
            </p>
            <p className="font-sans text-base text-white/55 leading-relaxed mb-5">
              Our mission is to help people feel seen, strengthened, and reminded that hope still exists &mdash; no matter what they are walking through. Whether you are a parent, a ministry leader, a business owner, a friend, or someone who simply cares about encouragement and healing &mdash; there is a place for you in this mission.
            </p>
            <p className="font-serif text-lg italic text-[#C59B76] mt-6">
              Every story shared is a seed planted. Every flyer posted is a hand extended.
            </p>
            <div className="w-12 h-[1px] bg-[#C59B76] mx-auto mt-8" />
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION 3: FLYER HUB ===== */}
      <section className="py-16 md:py-24 bg-white" data-testid="flyer-hub">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-3">
              Download &amp; Share
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-4">
              Printable Flyers
            </h2>
            <p className="font-sans text-base text-[#7A736E] max-w-xl mx-auto">
              Choose the flyer that fits your audience. Print it, post it, or share the digital version &mdash; every copy helps the mission reach further.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {flyerCategories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="flex flex-col rounded-2xl border border-[#E8E1D9] bg-[#FDFBF7] overflow-hidden"
                data-testid={`flyer-${cat.id}`}
              >
                <a
                  href={`/files/${cat.id}.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`flyer-${cat.id}-preview`}
                  className="block bg-white border-b border-[#E8E1D9] aspect-[4/5] overflow-hidden group"
                  aria-label={`Preview ${cat.title} flyer`}
                >
                  <img
                    src={`/files/${cat.id}-preview.jpg`}
                    alt={`${cat.title} flyer preview`}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </a>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-xl font-semibold text-[#2A2421] mb-2">
                    {cat.title}
                  </h3>
                  <p className="font-sans text-sm text-[#7A736E] leading-relaxed mb-5 flex-1">
                    {cat.desc}
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <a
                      href={`/files/${cat.id}.pdf`}
                      download={`LedByFaith-Flyer-${cat.id}.pdf`}
                      data-testid={`flyer-${cat.id}-pdf`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#A67A5B] text-[#A67A5B] text-xs font-semibold tracking-wide hover:bg-[#A67A5B] hover:text-white transition-all duration-300"
                    >
                      <FileText size={14} />
                      PDF
                    </a>
                    <a
                      href={`/files/${cat.id}.png`}
                      download={`LedByFaith-Flyer-${cat.id}.png`}
                      data-testid={`flyer-${cat.id}-png`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#A67A5B] text-[#A67A5B] text-xs font-semibold tracking-wide hover:bg-[#A67A5B] hover:text-white transition-all duration-300"
                    >
                      <Image size={14} />
                      PNG
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            className="mt-10 md:mt-14 rounded-2xl border border-[#E8E1D9] bg-[#FDFBF7] p-8 md:p-10 text-center"
            data-testid="private-request-note"
          >
            <div className="w-10 h-10 rounded-full bg-[#A67A5B]/10 flex items-center justify-center mx-auto mb-5">
              <Mail className="text-[#A67A5B]" size={18} />
            </div>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-3">
              By Request Only
            </p>
            <h3 className="font-serif text-2xl font-semibold text-[#2A2421] mb-4">
              Need a ministry or hospital-specific flyer?
            </h3>
            <p className="font-sans text-base text-[#7A736E] leading-relaxed max-w-xl mx-auto mb-6">
              For select sharing materials intended for ministry, hospital, or care-centered settings, please request below and we&rsquo;ll send them to you with care.
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="private-flyer-request-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#A67A5B] text-white text-sm font-medium tracking-wide hover:bg-[#8C654A] transition-all duration-300 shadow-md hover:shadow-lg"
            >
              <Mail size={15} />
              Request Private Flyer
            </button>
            <p className="font-sans text-xs text-[#7A736E]/60 mt-5 italic max-w-md mx-auto">
              These materials are shared privately to honor the settings and stories they carry.
            </p>
          </motion.div>

          <motion.p {...fadeUp} className="text-center font-sans text-xs text-[#7A736E]/50 mt-8">
            All public flyers are free to download, print, and share. Thank you for helping spread the mission.
          </motion.p>
        </div>
      </section>

      {/* ===== SECTION 4: SUBTLE CTA ===== */}
      <section className="py-14 md:py-20 bg-[#F5F2EB]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2A2421] mb-6">
              It Does Not Take Much
            </h2>
            <div className="space-y-4 font-sans text-base text-[#7A736E] leading-relaxed">
              <p>
                Printing a flyer takes a moment. Sharing a link takes a second. Leaving a card at a coffee shop, a church, a waiting room, or a community board costs nothing but intention.
              </p>
              <p>
                You never know who is quietly looking for something exactly like this &mdash; a journal to start their mornings with God, a story that makes them feel less alone, or a reminder that faith still carries people through the impossible.
              </p>
              <p className="text-[#2A2421]/70 font-medium">
                One share, one flyer, one conversation &mdash; that is how meaningful things grow.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION 5: EMAIL SIGNUP ===== */}
      <section className="py-16 md:py-24 bg-[#2A2421]" data-testid="stw-signup">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-3">
              Stay in the Loop
            </h2>
            <p className="font-sans text-sm text-white/50 mb-2 leading-relaxed max-w-lg mx-auto">
              Be the first to know when new books, journals, and purpose-driven projects are released.
            </p>
            <p className="font-sans text-xs text-white/30 mb-8">
              New releases. Meaningful updates. No spam.
            </p>
            <SignupForm />
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION 6: CLOSING GRATITUDE ===== */}
      <section className="py-14 md:py-20 bg-[#FDFBF7]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <div className="w-10 h-10 rounded-full bg-[#A67A5B]/10 flex items-center justify-center mx-auto mb-6">
              <Heart className="text-[#A67A5B]" size={20} />
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#2A2421] mb-5">
              Thank You
            </h2>
            <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-4">
              To everyone who has shared a flyer, posted a link, told a friend, or simply believed in this mission &mdash; thank you. No act of support is too small. Every share carries the potential to reach someone who needed it at exactly the right time.
            </p>
            <p className="font-sans text-base text-[#7A736E] leading-relaxed">
              Thank you for helping us spread something meaningful. And thank you for being part of this mission &mdash; in whatever way you can.
            </p>
            <p className="font-serif text-lg italic text-[#A67A5B]/70 mt-6">
              With faith and deep gratitude,<br />Led By Faith Studios
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function SignupForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'spread_the_word' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus({ type: 'success', message: data.message });
        setEmail('');
      } else {
        setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="stw-signup-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Your email address"
          data-testid="stw-signup-email"
          className="flex-1 bg-white/10 border border-white/20 focus:border-[#C59B76] text-white px-5 py-3 rounded-full outline-none transition-colors duration-300 placeholder:text-white/30 font-sans text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="stw-signup-submit"
          className="bg-[#C59B76] text-white px-6 py-3 rounded-full font-medium text-sm tracking-wide hover:bg-[#A67A5B] transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? 'Joining...' : 'Stay Connected'}
        </button>
      </form>
      {status && (
        <motion.p
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-xs mt-4 ${status.type === 'success' ? 'text-[#C59B76]' : 'text-red-400'}`}
        >
          {status.message}
        </motion.p>
      )}
    </div>
  );
}
